import { Modal, Input } from "antd";
import {
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
  InfoCircleOutlined,
  MinusOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useState, useEffect, useRef } from "react";
import type { TextAreaRef } from "antd/es/input/TextArea";
import {
  useMenuAddons,
  useMenuModifiers,
  useMenuItems,
} from "../../../../hooks/useMenu";
import type { Item, Addon, Modifier } from "../../../../api/menu";
import { getItemName, getTranslation } from "../../../../utils/translations";
import { useTranslation } from "react-i18next";
import { posModalStyles as modalStyles, KbdChip } from "../modalPrimitives";
import { useViewport } from "../../../../hooks/useViewport";
import { QuantityStepper } from "./QuantityStepper";
import { useQuantityInput } from "./useQuantityInput";

interface ModifyItemModalProps {
  item: Item | null;
  open: boolean;
  onClose: () => void;
  onAdd: (data: {
    quantity: number;
    // addonId -> { itemId -> количество }. При обикновените добавки (тип "1")
    // количеството се регулира с +/− на реда; при тип "2"/"3" винаги е 1.
    selectedAddonItems: Record<number, Record<number, number>>;
    selectedModifiers: number[];
    comment: string;
  }) => void;
  /** Отваря „Инфо" за продукта — подава се само на телефон (там футърът
      с Инфо бутона е скрит) и рендира info chip в хедъра на модала. */
  onInfo?: () => void;
  /** i18n ключ за primary бутона (получава {{price}}) — по подразбиране
      „Готово"; при повторна поръчка от историята се подава „Поръчай". */
  confirmLabelKey?: string;
  /** Начално състояние при отваряне (гласова поръчка) — вместо чистия ресет
      модалът се отваря с вече избраните от гласа количество/добавки/модификатори. */
  initial?: {
    quantity: number;
    selectedAddonItems: Record<number, Record<number, number>>;
    selectedModifiers: number[];
    comment: string;
  } | null;
}

export const ModifyItemModal = ({
  item,
  open,
  onClose,
  onAdd,
  onInfo,
  confirmLabelKey,
  initial,
}: ModifyItemModalProps) => {
  const { t, i18n } = useTranslation();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const {
    quantity,
    quantityInput,
    setQuantity,
    handleQuantityInputChange,
    decrement: handleDecrement,
    increment: handleIncrement,
  } = useQuantityInput();
  // На десктоп полето за коментар получава фокуса щом модалът се отвори (F9 /
  // „Промени") — човекът идва, за да напише бележка; количеството се сменя
  // със степера. Фокусът е след анимацията на antd Modal (afterOpenChange),
  // иначе диалогът си го взема обратно (същото като в EditModifiersModal). На
  // телефон/таблет не — екранната клавиатура би покрила добавките.
  const commentRef = useRef<TextAreaRef>(null);
  const [selectedAddonItems, setSelectedAddonItems] = useState<
    Record<number, Record<number, number>>
  >({});
  const [selectedModifiers, setSelectedModifiers] = useState<number[]>([]);
  const [comment, setComment] = useState("");

  const allAddons = useMenuAddons();
  const allModifiers = useMenuModifiers();
  const allItems = useMenuItems();

  const unsortedAddons: Addon[] = item
    ? allAddons.filter((a) => item.addonIds.includes(a.id))
    : [];

  const addonSortOrder = (addon: Addon): number => {
    if (addon.type === 2) return 0;
    if (addon.type === 3) return 2;
    if (addon.type === 1) return 3;
    return 1;
  };
  const addons = [...unsortedAddons].sort(
    (a, b) => addonSortOrder(a) - addonSortOrder(b),
  );
  const modifiers: Modifier[] = item
    ? allModifiers.filter((m) => item.modifierIds.includes(m.id))
    : [];

  // Брой РАЗЛИЧНИ избрани асортимента в добавка (количествата не влияят на лимита)
  const selectedCountOf = (addonId: number) =>
    Object.keys(selectedAddonItems[addonId] || {}).length;

  // Опциите на добавката, които реално са в менюто на ресторанта (останалите не се
  // показват по-долу, затова не може и да се изискват).
  const availableOptionsCountOf = (addon: Addon) =>
    addon.items.filter((option) => allItems.some((menuItem) => menuItem.id === option.itemId)).length;
  // Задължителната изисква maxQty различни опции, но не повече от наличните — иначе
  // „Добави“ остава заключен завинаги, без сервитьорът да вижда защо.
  const requiredCountOf = (addon: Addon) =>
    Math.min(addon.maxQty || 1, availableOptionsCountOf(addon));

  // Задължителна добавка без нито една налична опция не блокира продажбата.
  const requiredAddons = addons.filter(
    (a) => a.type === 2 && availableOptionsCountOf(a) > 0,
  );
  const allRequiredAddonsFulfilled = requiredAddons.every(
    (addon) => selectedCountOf(addon.id) >= requiredCountOf(addon),
  );

  // Тип "1" (обикновена) добавките са с АБСОЛЮТНО количество — не се умножават
  // по бройката на продукта. Тип "2"/"3" са по 1 на продукт (легаси) → × quantity.
  const { absoluteAddonsPrice, perProductAddonsPrice } = Object.entries(
    selectedAddonItems,
  ).reduce(
    (acc, [addonId, qtyMap]) => {
      const addon = addons.find((a) => a.id === Number(addonId));
      if (!addon) return acc;
      const groupSum = Object.entries(qtyMap).reduce((sum, [itemId, qty]) => {
        const addonItem = addon.items.find(
          (ai) => ai.itemId === Number(itemId),
        );
        if (!addonItem) return sum;
        const price = addonItem.addonItemPrice ?? addonItem?.price ?? 0;
        return sum + price * qty;
      }, 0);
      if (addon.type === 1) acc.absoluteAddonsPrice += groupSum;
      else acc.perProductAddonsPrice += groupSum;
      return acc;
    },
    { absoluteAddonsPrice: 0, perProductAddonsPrice: 0 },
  );

  const totalPrice = (
    ((item?.price ?? 0) + perProductAddonsPrice) * quantity +
    absoluteAddonsPrice
  ).toFixed(2);

  const handleAddToOrder = () => {
    onAdd({
      quantity,
      selectedAddonItems,
      selectedModifiers,
      comment: comment.trim(),
    });
    onClose();
    setQuantity(1);
    setSelectedAddonItems({});
    setSelectedModifiers([]);
    setComment("");
  };

  // Модалът се отваря чист (затваряне без „Добави" не оставя старо състояние) —
  // освен при подадено initial (гласова поръчка), което префилва избраното.
  useEffect(() => {
    if (!open) return;
    setQuantity(initial?.quantity ?? 1);
    setSelectedAddonItems(initial?.selectedAddonItems ?? {});
    setSelectedModifiers(initial?.selectedModifiers ?? []);
    setComment(initial?.comment ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Keyboard shortcuts — Enter потвърждава (добавя), Esc затваря.
  useEffect(() => {
    if (!open || !isDesktop) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLElement &&
        (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")
      ) {
        if (e.key === "Escape") {
          e.preventDefault();
          onClose();
        }
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        if (quantity > 0 && allRequiredAddonsFulfilled) handleAddToOrder();
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isDesktop, quantity, allRequiredAddonsFulfilled]);

  if (!item) return null;

  const itemName = getItemName(item.itemLangs, i18n.language, item.slug);

  const width = isMobile ? undefined : isTablet ? 520 : 640;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      afterOpenChange={(opened) => {
        if (opened && isDesktop) commentRef.current?.focus({ cursor: "end" });
      }}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={{
        ...modalStyles,
        content: { ...modalStyles.content, borderRadius: 20 },
      }}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div className="flex max-h-[85vh] flex-col bg-pos-card text-pos-ink">
        {/* Хедър — икон-chip + име на продукта + единична цена + close chip */}
        <div
          className={`flex items-center gap-3 border-b border-pos-line2 ${
            isNarrow ? "px-4 py-3.5" : "px-[22px] py-4"
          }`}
        >
          <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-[11px] bg-pos-ready-bg text-pos-accent">
            <EditOutlined className="text-[20px]" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="m-0 truncate text-lg font-extrabold leading-tight text-pos-ink">
              {itemName}
            </h2>
            <p className="m-0 mt-0.5 font-pos-mono text-[13px] text-pos-muted">
              {item.price.toFixed(2)} EUR
            </p>
          </div>
          {onInfo && (
            <button
              type="button"
              aria-label="info"
              onClick={onInfo}
              className="grid h-[38px] w-[38px] flex-shrink-0 cursor-pointer place-items-center rounded-[10px] border border-pos-line2 bg-pos-card text-pos-dim transition-colors hover:text-pos-ink"
            >
              <InfoCircleOutlined className="text-[15px]" />
            </button>
          )}
          <button
            type="button"
            aria-label="close"
            onClick={onClose}
            className="grid h-[38px] w-[38px] flex-shrink-0 cursor-pointer place-items-center rounded-[10px] border border-pos-line2 bg-pos-card text-pos-dim transition-colors hover:text-pos-ink"
          >
            <CloseOutlined className="text-[15px]" />
          </button>
        </div>

        <div
          className={`flex min-h-0 flex-col gap-[18px] overflow-y-auto ${
            isNarrow ? "p-4" : "px-6 py-5"
          }`}
        >
          {/* Количество — голям степер с редактируем четец по средата */}
          <QuantityStepper
            quantity={quantity}
            quantityInput={quantityInput}
            onInputChange={handleQuantityInputChange}
            onDecrement={handleDecrement}
            onIncrement={handleIncrement}
            isMobile={isMobile}
            // На десктоп фокусът при отваряне е в коментара (виж commentRef);
            // извън десктоп остава тук, както досега.
            autoFocus={!isDesktop}
          />

          {/* Addons — вертикален списък от редове (скролът идва от тялото на модала).
              Тип "1" (обикновена) има брояч +/− на реда; тип "2"/"3" са само чекбокс. */}
          {addons.map((addon) => {
            const addonName = getTranslation(
              addon.translations,
              i18n.language,
              addon.slug,
            );
            const selectedCount = selectedCountOf(addon.id);
            const maxQty = addon.maxQty || 999;
            const hasAvailableItems = addon.items.some((ai) =>
              allItems.find((i) => i.id === ai.itemId),
            );
            if (!hasAvailableItems) return null;
            const required = addon.type === 2;
            const recommended = addon.type === 3;
            const withQty = addon.type === 1;

            const toggleItem = (itemId: number, isSelected: boolean) => {
              setSelectedAddonItems((prev) => {
                const current = prev[addon.id] || {};
                if (isSelected) {
                  const { [itemId]: _, ...restItems } = current;
                  void _;
                  if (Object.keys(restItems).length === 0) {
                    const { [addon.id]: __, ...rest } = prev;
                    void __;
                    return rest;
                  }
                  return { ...prev, [addon.id]: restItems };
                }
                return { ...prev, [addon.id]: { ...current, [itemId]: 1 } };
              });
            };

            const changeItemQty = (itemId: number, delta: number) => {
              setSelectedAddonItems((prev) => {
                const current = prev[addon.id] || {};
                const newQty = (current[itemId] || 0) + delta;
                if (newQty <= 0) {
                  // Слизане под 1 = премахване на добавката
                  const { [itemId]: _, ...restItems } = current;
                  void _;
                  if (Object.keys(restItems).length === 0) {
                    const { [addon.id]: __, ...rest } = prev;
                    void __;
                    return rest;
                  }
                  return { ...prev, [addon.id]: restItems };
                }
                return {
                  ...prev,
                  [addon.id]: { ...current, [itemId]: newQty },
                };
              });
            };

            return (
              <div key={addon.id} className="flex flex-col gap-2.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-2 font-pos-mono text-[11px] font-semibold uppercase tracking-[.1em] text-pos-dim">
                    {addonName}
                    {/* Бадж на групата: изберете (задълж.) / препоръчани / по избор */}
                    <span
                      className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold normal-case tracking-normal ${
                        required
                          ? "bg-pos-ready-bg text-pos-ready"
                          : recommended
                            ? "bg-pos-marked text-pos-accent"
                            : "bg-pos-rowline text-pos-muted"
                      }`}
                    >
                      {required
                        ? t("ordering.addonRequired")
                        : recommended
                          ? t("ordering.addonRecommended")
                          : t("ordering.addonOptional")}
                    </span>
                  </span>
                  {required && (
                    <span className="font-pos-mono text-[11px] text-pos-muted">
                      {t("common.selected")}: {selectedCount} /{" "}
                      {requiredCountOf(addon)}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-1.5">
                  {addon.items.map((addonItem) => {
                    const addonItemData = allItems.find(
                      (i) => i.id === addonItem.itemId,
                    );
                    if (!addonItemData) return null;
                    const addonItemName = getItemName(
                      addonItemData.itemLangs,
                      i18n.language,
                      addonItemData.slug,
                    );
                    const itemQty =
                      selectedAddonItems[addon.id]?.[addonItem.itemId] || 0;
                    const isSelected = itemQty > 0;
                    // maxQty ограничава избора САМО при задължителна добавка (тип 2);
                    // обикновените и препоръчителните нямат лимит.
                    const canSelect =
                      !required || selectedCount < maxQty || isSelected;
                    const price =
                      addonItem.addonItemPrice ?? addonItem.price ?? 0;
                    const blocked = !canSelect && !isSelected;

                    // Редът е div, не button — вътре има бутони на брояча, а
                    // button в button е невалиден HTML. Затова Tab/Space идват
                    // от role="checkbox" + tabIndex, за да е достижим с
                    // клавиатурата като модификаторите. Enter НЕ се пипа тук:
                    // глобалният handler на модала го прави „Добави".
                    return (
                      <div
                        key={addonItem.itemId}
                        role="checkbox"
                        aria-checked={isSelected}
                        aria-disabled={blocked}
                        tabIndex={blocked ? -1 : 0}
                        onClick={() => {
                          if (blocked) return;
                          toggleItem(addonItem.itemId, isSelected);
                        }}
                        onKeyDown={(e) => {
                          if (e.key !== " " || e.target !== e.currentTarget)
                            return;
                          // Space иначе скролва тялото на модала
                          e.preventDefault();
                          if (blocked) return;
                          toggleItem(addonItem.itemId, isSelected);
                        }}
                        className={`flex items-center gap-3 rounded-xl border px-3.5 py-[11px] transition-colors ${
                          isSelected
                            ? "border-pos-accent bg-pos-marked"
                            : "border-pos-line3 bg-pos-card"
                        } ${
                          blocked
                            ? "cursor-not-allowed opacity-45"
                            : "cursor-pointer"
                        }`}
                      >
                        {/* Чекбокс */}
                        <AddonCheckbox checked={isSelected} />

                        {/* Име */}
                        <span className="min-w-0 flex-1 truncate text-left text-sm font-semibold text-pos-ink">
                          {addonItemName}
                        </span>

                        {/* Цена (единична) */}
                        <span className="flex-shrink-0 font-pos-mono text-[13px] font-semibold text-pos-accent-ink">
                          +{price.toFixed(2)}
                        </span>

                        {/* Брояч — само при обикновена добавка (тип "1") и избран ред */}
                        {withQty && isSelected && (
                          <span
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex flex-shrink-0 items-stretch overflow-hidden rounded-[10px] border border-pos-ctl bg-pos-card"
                          >
                            <button
                              type="button"
                              onClick={() =>
                                changeItemQty(addonItem.itemId, -1)
                              }
                              className="grid h-8 w-8 cursor-pointer place-items-center bg-pos-stepper text-pos-dim"
                            >
                              <MinusOutlined className="text-[11px]" />
                            </button>
                            <span className="grid min-w-[34px] place-items-center font-pos-mono text-[15px] font-semibold text-pos-ink">
                              {itemQty}
                            </span>
                            <button
                              type="button"
                              onClick={() => changeItemQty(addonItem.itemId, 1)}
                              className="grid h-8 w-8 cursor-pointer place-items-center bg-pos-stepper text-pos-accent-ink"
                            >
                              <PlusOutlined className="text-[11px]" />
                            </button>
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Modifiers — редове с чекбокс, същият език като добавките */}
          {modifiers.length > 0 && (
            <div className="flex flex-col gap-2.5">
              <span className="font-pos-mono text-[11px] font-semibold uppercase tracking-[.1em] text-pos-dim">
                {t("ordering.modifiers")}
              </span>
              <div className="flex flex-col gap-1.5">
                {modifiers.map((modifier) => {
                  const modifierName = getTranslation(
                    modifier.translations,
                    i18n.language,
                    modifier.slug,
                  );
                  const isSelected = selectedModifiers.includes(modifier.id);
                  return (
                    <button
                      type="button"
                      key={modifier.id}
                      onClick={() => {
                        setSelectedModifiers((prev) =>
                          prev.includes(modifier.id)
                            ? prev.filter((id) => id !== modifier.id)
                            : [...prev, modifier.id],
                        );
                      }}
                      className={`flex w-full cursor-pointer items-center gap-3 rounded-xl border px-3.5 py-[11px] transition-colors ${
                        isSelected
                          ? "border-pos-accent bg-pos-marked"
                          : "border-pos-line3 bg-pos-card"
                      }`}
                    >
                      <AddonCheckbox checked={isSelected} />
                      <span className="min-w-0 flex-1 truncate text-left text-sm font-semibold text-pos-ink">
                        {modifierName}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Comment */}
          <div className="flex flex-col gap-2.5">
            <span className="font-pos-mono text-[11px] font-semibold uppercase tracking-[.1em] text-pos-dim">
              {t("ordering.itemComment")}
            </span>
            <Input.TextArea
              ref={commentRef}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t("ordering.itemCommentPlaceholder")}
              // Enter в коментара тригерва „Добави" (shortcut ⏎), вместо нов ред;
              // Shift+Enter остава за нов ред.
              onPressEnter={(e) => {
                if (e.shiftKey) return;
                e.preventDefault();
                if (quantity > 0 && allRequiredAddonsFulfilled) {
                  handleAddToOrder();
                }
              }}
              autoSize={{ minRows: 1, maxRows: 5 }}
              maxLength={200}
              className="!resize-none !rounded-xl !border-pos-ctl !bg-pos-panel !px-[15px] !py-[13px] !text-[15px] !leading-normal !text-pos-ink placeholder:!text-pos-muted"
            />
          </div>
        </div>

        {/* Футър — ghost „Назад" + primary „Добави · {сума}" (гейтът е непроменен;
            при повторна поръчка от историята етикетът е „Поръчай" през confirmLabelKey) */}
        <div
          className={`flex gap-2.5 border-t border-pos-line2 ${
            isNarrow ? "px-4 py-3" : "px-[22px] py-3.5"
          }`}
        >
          <button
            type="button"
            onClick={onClose}
            className="relative h-[54px] flex-1 cursor-pointer rounded-[14px] border border-pos-ctl bg-pos-card text-[15px] font-semibold text-pos-ink2 transition-colors hover:bg-pos-panel"
          >
            {t("common.back")}
            {isDesktop && <KbdChip shortcut="Esc" colorClass="text-pos-faint" />}
          </button>
          <button
            type="button"
            onClick={handleAddToOrder}
            disabled={quantity <= 0 || !allRequiredAddonsFulfilled}
            className="relative h-[54px] flex-[2] cursor-pointer rounded-[14px] bg-pos-accent text-base font-bold text-white transition-colors hover:bg-pos-accent-ink disabled:cursor-not-allowed disabled:opacity-45"
          >
            {t(confirmLabelKey ?? "ordering.modifyDone", { price: totalPrice })}
            {isDesktop && (
              // ⏎ е Unicode глиф, който рендира по-дребно от латиница — бъмпваме
              // размера, за да съвпадне визуално с "Esc" на съседния бутон.
              <span className="absolute right-2 top-[3px] font-pos-mono text-[14px] font-semibold leading-none text-white/60">
                ⏎
              </span>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// Квадратен чекбокс 22×22 за редовете на добавки/модификатори
// (преизползва се и от EditModifiersModal)
export const AddonCheckbox = ({ checked }: { checked: boolean }) => (
  <span
    className={`grid h-[22px] w-[22px] flex-shrink-0 place-items-center rounded-[7px] border-2 ${
      checked
        ? "border-pos-accent bg-pos-accent text-white"
        : "border-pos-check bg-pos-card"
    }`}
  >
    {checked && <CheckOutlined className="text-[12px]" />}
  </span>
);
